export const inputTypes = {
  SCORING_MODEL: 'scoring_model',
  BOOLEAN: 'boolean',
  MULTI_SELECT: 'multi_select',
  SINGLE_SELECT: 'single_select',
  SCORE_MODEL: 'score_model',
  BENEFICIAL_OWNERS: 'beneficial_owners',
  GLOBAL_DATA: 'global_data',
  // Risika for AI: the allow_ai toggle plus its end date (ENG2-2614).
  RISIKA_AI: 'risika_ai',
}

// Risika for AI end date (ENG2-2614 / parent ENG2-2553). Lives under
// rights.global next to the Global Data dates and is only shown while the
// allow_ai toggle is on. RisikaAPI switches Risika for AI off after this date;
// null means no end. The API fills it (today + 14) when a customer is created
// on or moved to TRIAL_MCP and clears it on any other plan change, so CMD only
// shows and edits it.
export const aiEndDateConfig = {
  label: 'Risika for AI ends',
  key: 'ai_end_date',
  updateLocation: 'global',
}

// `section` places a right in one of the two Rights groups (ENG2-2457 / T2,
// memberships from T0 / D4): 'included' = on for every 2026 package, 'addon' =
// off by default and sold separately. `hidden` keeps a right in the model (it is
// still a restriction column, compared by Restore Defaults) without rendering
// it. Display order lives in RIGHTS_DISPLAY_ORDER below.
export type RightSection = 'included' | 'addon'

export const booleans: {
  label: string
  key: string
  type: string
  updateLocation?: string
  section: RightSection
  hidden?: boolean
  // A date sub-config rendered under the toggle while it is on (Risika for AI).
  endDate?: { label: string; key: string; updateLocation?: string }
}[] = [
  {
    // ENG2-2454 (T1): shown as "Risk Insights"; the key is unchanged.
    label: 'Risk Insights',
    key: 'observational_lists',
    updateLocation: 'global',
    type: inputTypes.BOOLEAN,
    section: 'addon',
  },
  {
    label: 'Ean',
    key: 'allow_ean',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Monitoring',
    key: 'allow_monitoring',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Api',
    key: 'allow_api',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    // Legacy: CS asked for it to disappear from the dashboard (ENG2-2457).
    label: 'Browser Extension',
    key: 'allow_browser_extension',
    type: inputTypes.BOOLEAN,
    section: 'included',
    hidden: true,
  },
  {
    label: 'Company Search',
    key: 'allow_company_search',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Person Search',
    key: 'allow_person_search',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Company Export',
    key: 'allow_company_export',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Person Export',
    key: 'allow_person_export',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Monitoring Export',
    key: 'allow_monitoring_export',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Relations',
    key: 'allow_relations',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Highlights',
    key: 'allow_highlights',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },

  {
    label: 'Basic',
    key: 'allow_basic',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Financial',
    key: 'allow_financial',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Credit',
    key: 'allow_credit',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Hierarchy',
    key: 'allow_hierarchy',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'PDF Reports',
    key: 'allow_pdf_reports',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    label: 'Notes',
    key: 'allow_notes',
    type: inputTypes.BOOLEAN,
    section: 'included',
  },
  {
    // ENG2-2454 (T1): shown as "Credit Automator"; the key is unchanged.
    label: 'Credit Automator',
    key: 'enterprise_credit_policy',
    updateLocation: 'global',
    type: inputTypes.BOOLEAN,
    section: 'addon',
  },
  {
    label: 'Fraud Indicators',
    key: 'fraud_indicators',
    updateLocation: 'global',
    type: inputTypes.BOOLEAN,
    section: 'addon',
  },
  {
    // ENG2-2454 (T1): shown as "Credit Requests"; the key is unchanged.
    label: 'Credit Requests',
    key: 'credit_override',
    updateLocation: 'global',
    type: inputTypes.BOOLEAN,
    section: 'addon',
  },
  {
    // ENG2-2542: a top-level right like allow_api (RisikaAPI ENG2-2540), so
    // no updateLocation. ENG2-2614: rendered as a toggle plus the end date
    // under rights.global (RisikaAi input), like Global Data and its dates.
    label: 'Risika for AI',
    key: 'allow_ai',
    type: inputTypes.RISIKA_AI,
    section: 'addon',
    endDate: aiEndDateConfig,
  },
  {
    // ENG2-2815: a top-level right like allow_ai (RisikaAPI #3184), off in every
    // plan and switched on per customer. It gates POST /{country}/signals.
    label: 'Sales Signals',
    key: 'allow_sales_triggers',
    type: inputTypes.BOOLEAN,
    section: 'addon',
  },
]

// Global Data groups the toggle with the two contract dates (ENG2-2395); both
// are only shown while the toggle is on (rendered by the GlobalData input).
//
// The dates mean different things on the backend (shared/GlobalDataSubscription.py):
// the start date is informational and never gates a lookup, while the end date is
// what actually closes access — leaving it empty means the contract auto-renews.
export const globalDataConfig = {
  label: 'Global Data',
  key: 'global_data',
  type: inputTypes.GLOBAL_DATA,
  updateLocation: 'global',
  section: 'addon' as RightSection,
  startDate: {
    label: 'Subscription start date',
    key: 'global_data_subscription_start_date',
    updateLocation: 'global',
  },
  endDate: {
    label: 'Subscription end date',
    key: 'global_data_subscription_end_date',
    updateLocation: 'global',
  },
}

export const beneficialOwnersConfig = {
  label: 'Beneficial Owners',
  key: 'beneficial_owners',
  type: inputTypes.BENEFICIAL_OWNERS,
  updateLocation: 'global',
  section: 'addon' as RightSection,
  reasonOptions: [
    { label: 'CDD', key: 'CDD' },
    { label: 'Sanctions', key: 'SANCTIONS_SCREENING' },
    { label: 'Credit risk', key: 'CREDIT_RISK' },
  ],
}

// Order inside each Rights group, as given by CS in T0 / D4 (ENG2-2457).
export const RIGHTS_DISPLAY_ORDER: Record<RightSection, string[]> = {
  included: [
    'allow_api',
    'allow_company_search',
    'allow_person_search',
    'allow_relations',
    'allow_highlights',
    'allow_basic',
    'allow_financial',
    'allow_credit',
    'allow_hierarchy',
    'allow_notes',
    'allow_company_export',
    'allow_person_export',
    'allow_monitoring_export',
    'allow_pdf_reports',
    'allow_ean',
    'allow_monitoring',
  ],
  addon: [
    'observational_lists',
    'fraud_indicators',
    'global_data',
    'enterprise_credit_policy',
    'beneficial_owners',
    'credit_override',
    'allow_ai',
    'allow_sales_triggers',
  ],
}

export const countrySpecific = [
  {
    label: 'Score models',
    key: 'score_models',
    type: inputTypes.SCORE_MODEL,
    options: [
      { label: 'BOHR', key: 'BOHR' },
      { label: 'NEWTON', key: 'NEWTON' },
    ],
  },
]
export const countriesForCountrySpecific = [
  {
    country: 'Denmark',
    countryCode: 'DK',
    availableModels: [
      { model: 'BOHR', canBeChanged: false },
      { model: 'NEWTON', canBeChanged: true },
    ],
  },
  {
    country: 'Faroe Islands',
    countryCode: 'FO',
    availableModels: [{ model: 'BOHR', canBeChanged: false }],
  },
  {
    country: 'Norway',
    countryCode: 'NO',
    availableModels: [
      { model: 'BOHR', canBeChanged: false },
      { model: 'NEWTON', canBeChanged: true },
    ],
  },
  {
    country: 'Sweden',
    countryCode: 'SE',
    availableModels: [{ model: 'BOHR', canBeChanged: false }],
  },
  {
    country: 'Finland',
    countryCode: 'FI',
    availableModels: [{ model: 'BOHR', canBeChanged: false }],
  },
]

export const selects = [
  {
    label: 'Countries',
    key: 'allow_countries',
    type: inputTypes.MULTI_SELECT,
    options: [
      { label: 'DK', key: 'DK' },
      { label: 'FO', key: 'FO' },
      { label: 'SE', key: 'SE' },
      { label: 'NO', key: 'NO' },
      { label: 'FI', key: 'FI' },
    ],
  },
  {
    label: 'Credit Policy',
    key: 'allow_credit_policy',
    type: inputTypes.SINGLE_SELECT,
    options: [
      { label: 'None', key: 'None' },
      { label: 'Simple', key: 'Simple' },
      { label: 'Advanced', key: 'Advanced' },
    ],
  },
  {
    label: 'Tier',
    key: 'tier',
    type: inputTypes.SINGLE_SELECT,
    options: [
      { label: '1', key: 1 },
      { label: '2', key: 2 },
      { label: '3', key: 3 },
      { label: '4', key: 4 },
    ],
  },
]

// The Counters & Limits rows now live in usage-table/usage-rows.ts (ENG2-2461).

export const tooltipData = {
  max_users: 'Current users / Alter maximum users amount',

  allow_api: 'Allow the company to make API integrations',
  allow_ai:
    "Risika for AI: lets the customer's users connect Microsoft 365 Copilot, Claude and other AI assistants to Risika through the hosted MCP server.",
  allow_basic: 'General company information (Name, address etc..)',
  allow_browser_extension: 'Enable/Disable the chrome extension',
  allow_company_export: 'Filtering export',
  allow_company_search: 'Search company',
  allow_countries: 'Which countries can the customer use',
  allow_credit: 'Credit rating (Credit max, Credit days)',
  allow_credit_policy: 'Which policy is the customer allowed to use',
  allow_ean: 'Numbers are shown in general company information',
  allow_financial: 'Key financial figures',
  allow_hierarchy: 'Company hierarchy',
  allow_highlights: 'Company highlights',
  allow_models: 'Which model can a company use',
  allow_monitoring: 'If they can add companies to the monitoring list',
  allow_monitoring_export: 'Are you allowed to export your monitoring lists',
  allow_notes: 'Internal Ids, phone numbers',
  allow_pdf_reports: 'Pdf report from the credit check page',
  allow_person_export: 'If you are allowed to export people (Not implemented)',
  allow_person_search: 'Person search',
  allow_relations: 'Company and person relations',
  allow_sales_triggers:
    'Sales Signals: lets the customer call the signals API endpoint. The sales_signals tool in the hosted MCP server also needs Risika for AI on. A change takes effect when the customer gets a new token.',
  search_exports_limit: 'Maximum amount of rows that can be exported at once',
  tier: 'How often can they do requests on the API (Ask Troles first)',

  credit_ratings:
    'Amount of times a company can get recommended credit limit and credit days',
  credit_reports: 'Amount of reports that can be downloaded',
  lookups: 'Amount of times a company can see General company information',
  monitoring_count: 'Amount of companies monitored',
  monitoring_exports:
    'How many times can they export a list in monitoring (no matter the size of the list)',
  search_exports:
    'How many times can they export a list in filtering (no matter the size of the list)',
  beneficial_owners:
    'Enable access to beneficial owners data. Requires selecting a default legitimate reason (CDD, Sanctions, Credit risk).',
}

export const categoriesData = {
  users: {
    title: 'Users',
    fields: ['max_users'],
  },
  rights: {
    title: 'Rights',
    fields: [
      'allow_api',
      'allow_basic',
      'allow_browser_extension',
      'allow_company_export',
      'allow_company_search',
      'allow_countries',
      'allow_credit',
      'allow_credit_policy',
      'allow_ean',
      'allow_financial',
      'allow_hierarchy',
      'allow_highlights',
      'allow_monitoring',
      'allow_monitoring_export',
      'allow_notes',
      'allow_pdf_reports',
      'allow_person_export',
      'allow_person_search',
      'allow_relations',
      'search_exports_limit',
      'tier',
    ],
  },
  countersCaps: {
    title: 'Counters / Caps',
    fields: [
      'credit_ratings',
      'credit_reports',
      'lookups',
      'monitoring_count',
      'monitoring_exports',
      'search_exports',
    ],
  },
  models: {
    title: 'Available models',
    fields: ['DK', 'SE', 'NO', 'FI'],
  },
}
